// src/components/pages/AddExpense.js - Updated with Notifications
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { API_BASE } from '../../utils/constants';
import { EXPENSE_CATEGORIES } from '../../utils/expenseCategories';
const AddExpense = () => {
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({
    group: '',
    description: '',
    amount: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  const loadGroups = async () => {
    try {
      const res = await fetch(`${API_BASE}/groups/user/email/${user.email}`);
      const groupData = await res.json();
      setGroups(Array.isArray(groupData) ? groupData : []);
      
      if (groupData.length === 0 && window.showNotification) {
        window.showNotification('info', 'You need to create a group first before adding expenses.');
      }
    } catch (err) {
      console.error('Failed to fetch groups', err);
      if (window.showNotification) {
        window.showNotification('error', 'Failed to load your groups.');
      }
    }
  };

  useEffect(() => {
    if (user) {
      loadGroups();
    }
  }, [user]);

  // Validation function for amount
  const validateAmount = (amount) => {
    const errors = [];
    
    if (!amount || amount.toString().trim().length === 0) {
      errors.push('Amount is required');
      return errors;
    }
    
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount)) {
      errors.push('Amount must be a valid number');
    } else {
      if (numAmount <= 0) {
        errors.push('Amount must be greater than zero');
      }
      
      if (numAmount > 999999.99) {
        errors.push('Amount is too large (maximum: ₹999,999.99)');
      }
      
      if (amount.toString().includes('.') && amount.toString().split('.')[1].length > 2) {
        errors.push('Amount can have at most 2 decimal places');
      }
    }
    
    return errors;
  };

  // Validation function for description
  const validateDescription = (description) => {
    const errors = [];
    
    if (!description || description.trim().length === 0) {
      errors.push('Description is required');
      return errors;
    }
    
    const trimmed = description.trim();
    
    if (trimmed.length < 2) {
      errors.push('Description must be at least 2 characters long');
    }
    
    if (trimmed.length > 100) {
      errors.push('Description must be less than 100 characters');
    }
    
    return errors;
  };

  // Real-time validation handlers
  const handleAmountChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, amount: value });
    
    const amountErrors = validateAmount(value);
    setErrors({
      ...errors,
      amount: amountErrors.length > 0 ? amountErrors : null
    });
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, description: value });
    
    const descErrors = validateDescription(value);
    setErrors({
      ...errors,
      description: descErrors.length > 0 ? descErrors : null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const amountErrors = validateAmount(formData.amount);
    const descErrors = validateDescription(formData.description);
    
    const allErrors = {
      amount: amountErrors.length > 0 ? amountErrors : null,
      description: descErrors.length > 0 ? descErrors : null
    };
    
    // Check if group is selected
    if (!formData.group) {
      allErrors.group = ['Please select a group'];
    }
    
    setErrors(allErrors);
    
    // If there are any errors, stop submission
    if (Object.values(allErrors).some(error => error !== null)) {
      if (window.showNotification) {
        window.showNotification('error', 'Please fix the form errors before submitting.');
      }
      return;
    }
    
    setLoading(true);

    try {
      const userRes = await fetch(`${API_BASE}/users/email/${user.email}`);
      if (!userRes.ok) {
        throw new Error('Failed to fetch user data');
      }
      const userData = await userRes.json();

      const payload = {
        groupId: parseInt(formData.group),
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
        payerId: userData.id
      };

      console.log('Sending expense payload:', payload);

      const res = await fetch(`${API_BASE}/expenses/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const response = await res.text();
        if (window.showNotification) {
          window.showNotification('success', response);
        }
        navigate('/dashboard');
      } else {
        const errorText = await res.text();
        if (window.showNotification) {
          window.showNotification('error', `Failed to add expense: ${errorText}`);
        }
      }
    } catch (err) {
      console.error('Error adding expense:', err);
      if (window.showNotification) {
        window.showNotification('error', 'Something went wrong. Please try again.');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Add a New Expense</h1>
          <p className="mt-1 text-sm text-gray-600">
            Record a new expense and split it with your group members.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          <div>
            <label htmlFor="group" className="block text-sm font-medium text-gray-700">
              Select Group *
            </label>
            <select
              id="group"
              required
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 ${
                errors.group ? 'border-red-300' : 'border-gray-300'
              }`}
              value={formData.group}
              onChange={(e) => {
                setFormData({ ...formData, group: e.target.value });
                if (errors.group) {
                  setErrors({ ...errors, group: null });
                }
              }}
            >
              <option value="">Choose a group</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            {errors.group && (
              <div className="mt-2">
                {errors.group.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {error}
                  </p>
                ))}
              </div>
            )}
            {groups.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ You don't have any groups yet. 
                  <button
                    type="button"
                    onClick={() => navigate('/create-group')}
                    className="ml-2 text-yellow-900 underline hover:no-underline"
                  >
                    Create a group first
                  </button>
                </p>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description *
            </label>
            <input
              type="text"
              id="description"
              required
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="E.g., Dinner at Pizza Hut, Uber ride, Grocery shopping"
              value={formData.description}
              onChange={handleDescriptionChange}
            />
            {errors.description && (
              <div className="mt-2">
                {errors.description.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>
<div>
  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
    Category (Optional)
   </label>
   <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
    {EXPENSE_CATEGORIES.map((category) => {
      const IconComponent = category.icon;
      return (
        <button
          key={category.id}
          type="button"
          onClick={() => setFormData({ ...formData, category: category.id })}
          className={`p-3 rounded-md border-2 transition-all ${
            formData.category === category.id
              ? 'border-teal-500 bg-teal-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${category.color}`}>
            <IconComponent className="h-4 w-4" />
          </div>
          <span className="text-xs font-medium text-center block">{category.name}</span>
        </button>
      );
    })}
  </div>
  </div>
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
              Amount (₹) *
            </label>
            <input
              type="number"
              id="amount"
              required
              step="0.01"
              min="0.01"
              max="999999.99"
              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 ${
                errors.amount ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="E.g., 1500.50"
              value={formData.amount}
              onChange={handleAmountChange}
            />
            {errors.amount && (
              <div className="mt-2">
                {errors.amount.map((error, index) => (
                  <p key={index} className="text-sm text-red-600 flex items-center">
                    <span className="mr-1">⚠️</span>
                    {error}
                  </p>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-500">
              <p>💡 <strong>Rules:</strong> Must be greater than ₹0, maximum ₹999,999.99, up to 2 decimal places</p>
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Amount Selection
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[100, 500, 1000, 2000].map((quickAmount) => (
                <button
                  key={quickAmount}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, amount: quickAmount.toString() });
                    setErrors({ ...errors, amount: null });
                  }}
                  className="py-2 px-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  ₹{quickAmount}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || 
                !formData.group || 
                (errors.amount && errors.amount.length > 0) ||
                (errors.description && errors.description.length > 0) ||
                (errors.group && errors.group.length > 0) ||
                groups.length === 0
              }
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                'Add Expense'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpense;